import { StyleSheet } from 'aphrodite';
import { SPACE, THEME } from '../../../web/components-v2/Theme/theme';

const { colors, spacing, typography, common } = THEME;

export const styleSheet = StyleSheet.create({
	page: {
		display: 'flex',
		flexDirection: 'column',
		gap: spacing.lg,
		padding: spacing.lg,
	},
	pageHeader: {
		display: 'flex',
		alignItems: 'flex-start',
		justifyContent: 'space-between',
		gap: spacing.md,
		flexWrap: 'wrap',
	},
	pageTitle: {
		fontFamily: typography.fontFamilies.sora,
		fontSize: typography.fontSizes.xl,
		fontWeight: typography.fontWeights.bold,
		color: colors.text.main,
		margin: 0,
	},
	pageSubtitle: {
		fontSize: typography.fontSizes.sm,
		color: colors.text.light,
		margin: `${spacing.xs}px 0 0`,
	},
	controlsRow: {
		display: 'flex',
		alignItems: 'center',
		gap: spacing.sm,
		flexWrap: 'wrap',
	},
	searchInput: {
		width: 260,
	},
	filterGroup: {
		display: 'flex',
		alignItems: 'center',
		gap: spacing.sm,
		fontSize: typography.fontSizes.sm,
		color: colors.text.light,
	},
	statRow: {
		display: 'grid',
		gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
		gap: spacing.md,
		[THEME.queries.sm]: {
			gridTemplateColumns: '1fr',
		},
	},
	statCard: {
		display: 'flex',
		flexDirection: 'column',
		gap: spacing.xs,
		padding: spacing.md,
		backgroundColor: colors.background.light,
		border: common.border,
		borderRadius: common.borderRadius,
	},
	statLabel: {
		fontSize: typography.fontSizes.xs,
		fontWeight: typography.fontWeights.semibold,
		color: colors.text.light,
		textTransform: 'uppercase',
		letterSpacing: 0.4,
	},
	statValue: {
		fontFamily: typography.fontFamilies.sora,
		fontSize: typography.fontSizes.xxl,
		fontWeight: typography.fontWeights.bold,
	},
	statSuspended: {
		color: colors.danger.main,
	},
	statPending: {
		color: colors.warning.dark,
	},
	statMigrated: {
		color: colors.success.dark,
	},
	section: {
		backgroundColor: colors.background.light,
		border: common.border,
		borderRadius: common.borderRadius,
		overflow: 'hidden',
	},
	sectionHeader: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: spacing.sm,
		padding: spacing.md,
		flexWrap: 'wrap',
	},
	sectionHeaderLeft: {
		display: 'flex',
		alignItems: 'center',
		gap: spacing.sm,
	},
	sectionTitle: {
		fontFamily: typography.fontFamilies.sora,
		fontSize: typography.fontSizes.lg,
		fontWeight: typography.fontWeights.bold,
		color: colors.text.main,
		margin: 0,
	},
	sectionMeta: {
		fontSize: typography.fontSizes.sm,
		color: colors.text.light,
	},
	emptyState: {
		padding: spacing.lg,
		fontSize: typography.fontSizes.sm,
		color: colors.text.light,
		textAlign: 'center' as const,
	},
	skeletonLine: {
		height: spacing.md,
		width: '60%',
		margin: `0 auto ${spacing.sm}px`,
	},
	skeletonLineShort: {
		width: '40%',
		marginBottom: 0,
	},
	table: {
		width: '100%',
		borderCollapse: 'collapse' as const,
		fontSize: typography.fontSizes.sm,
		// Each provider renders its own table, so widths must be declared to line up across them.
		tableLayout: 'fixed' as const,
	},
	colAccount: {
		width: '24%',
	},
	colError: {
		width: '46%',
	},
	colSuspended: {
		width: '14%',
	},
	colActions: {
		width: '16%',
	},
	th: {
		textAlign: 'left' as const,
		padding: `${spacing.sm}px ${spacing.md}px`,
		fontSize: typography.fontSizes.xs,
		fontWeight: typography.fontWeights.semibold,
		color: colors.text.main,
		textTransform: 'uppercase',
		letterSpacing: 0.3,
		backgroundColor: colors.hover.light,
		borderBottom: common.border,
	},
	td: {
		padding: `${spacing.sm}px ${spacing.md}px`,
		borderBottom: `1px solid ${colors.border.light}`,
		verticalAlign: 'top' as const,
		overflowWrap: 'anywhere' as const,
	},
	accountLink: {
		fontWeight: typography.fontWeights.semibold,
	},
	mutedText: {
		color: colors.text.light,
	},
	actionButton: {
		display: 'inline-flex',
	},
	reasonText: {
		display: 'block',
		color: colors.danger.dark,
	},

	statSetupIncomplete: {
		color: colors.warning.dark,
	},
	statCardFlagged: {
		borderColor: colors.warning.main,
		backgroundColor: colors.warning.light,
	},

	setupCell: {
		display: 'flex',
		alignItems: 'flex-start',
		gap: SPACE[4],
	},
	setupTrigger: {
		position: 'relative',
		cursor: 'help',
		flexShrink: 0,
		height: 'auto',
		width: 'auto',
		padding: SPACE[4],
	},
	setupIcon: {
		width: 16,
		height: 16,
	},
	setupIndicator: {
		position: 'absolute',
		// Optical nudge so the dot sits on the gear's top-right edge rather than beside it.
		top: -2,
		right: -2,
		width: SPACE[8],
		height: SPACE[8],
		borderRadius: '50%',
		backgroundColor: colors.warning.dark,
		border: `2px solid ${colors.background.light}`,
	},
	setupLabel: {
		color: colors.warning.dark,
		fontWeight: typography.fontWeights.semibold,
	},
	setupTooltip: {
		maxWidth: 320,
		textAlign: 'left' as const,
	},
	setupTooltipHeading: {
		display: 'block',
		fontWeight: typography.fontWeights.bold,
		marginBottom: SPACE[4],
	},
	setupTooltipList: {
		display: 'flex',
		flexDirection: 'column',
		gap: SPACE[4],
		margin: 0,
		paddingLeft: SPACE[16],
	},
	setupTooltipFootnote: {
		display: 'block',
		marginTop: SPACE[8],
		opacity: 0.8,
	},

	toolbar: {
		display: 'flex',
		alignItems: 'center',
		gap: SPACE[8],
		padding: `${SPACE[8]}px ${SPACE[16]}px`,
		borderBottom: common.border,
		flexWrap: 'wrap',
	},
	toolbarSpacer: {
		marginLeft: 'auto',
	},
	selectionCount: {
		fontSize: typography.fontSizes.sm,
		color: colors.text.light,
	},
	retryHint: {
		fontSize: typography.fontSizes.xs,
		color: colors.text.light,
	},
	selectCell: {
		width: 44,
	},
	providerRatio: {
		fontSize: typography.fontSizes.xs,
		color: colors.text.light,
		fontWeight: typography.fontWeights.standard,
	},
});
